import { createAuthClient } from 'better-auth/react';
import { inferAdditionalFields } from 'better-auth/client/plugins';
import { hadSession, markHadSession, reportClientDiag } from './sessionDiagnostics';
import { getAuthToken, isNative, setAuthToken } from './platform';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SESSION_FETCH_MAX_RETRIES = 3;
const SESSION_FETCH_RETRY_BASE_MS = 300;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const requestUrl = (input: string | URL | Request): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

// TEMPORARY DIAGNOSTICS (2026-06-10, iOS cold-load login race): surface every
// suspicious get-session outcome in the API logs. A 200 whose body is the
// literal `null` on a browser that previously had a session is the exact
// failure being chased — the server said "logged out" to a logged-in user.
const reportGetSessionOutcome = (response: Response, attempt: number) => {
  if (response.status !== 200) {
    reportClientDiag('get-session-non-200', { status: response.status, attempt });
    return;
  }

  void response
    .clone()
    .text()
    .then((body) => {
      if (body === 'null') {
        if (hadSession()) {
          reportClientDiag('get-session-null-with-prior-session', { attempt });
        }
        return;
      }
      // A 200 with a session body — mark the hint here at the fetch layer so
      // devices that logged in before this code shipped gain protection on
      // their first successful load, not their second.
      markHadSession();
    })
    .catch(() => undefined);
};

// On a cold (typically mobile) connection the first /get-session request can fail at the
// network layer (DNS/TLS/TCP reset) or hit a transient 5xx before the API is warm. Better
// Auth has no retry, so a single such failure resolves the session to null and the app
// renders the login screen for an already-authenticated user — recoverable only by a manual
// refresh. better-fetch's built-in `retry` covers HTTP statuses but NOT network rejections,
// so we wrap fetch to retry both. A real 4xx (e.g. a 401 "no session") is never retried, so
// genuinely logged-out users are not delayed. Only idempotent reads are retried.
// Native (Capacitor) transport: the WebView origin can't hold cross-site
// cookies, so sessions ride a bearer token instead — attached on every auth
// request, and captured from the `set-auth-token` header the better-auth
// bearer plugin emits on sign-in/sign-up responses.
const withNativeAuth = async (init?: RequestInit): Promise<RequestInit | undefined> => {
  if (!isNative()) return init;
  const token = await getAuthToken();
  if (!token) return init;
  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers };
};

// Awaited (not fire-and-forget): the native sign-in flow hard-reloads the
// WebView right after sign-in resolves, so the token must be durably in
// Preferences before the response is handed back to the caller.
const captureNativeAuthToken = async (response: Response): Promise<void> => {
  if (!isNative()) return;
  const token = response.headers.get('set-auth-token');
  if (token) {
    await setAuthToken(token);
  }
};

const retryingFetch = async (
  input: string | URL | Request,
  init?: RequestInit,
): Promise<Response> => {
  const method = (init?.method ?? 'GET').toUpperCase();
  const isIdempotent = method === 'GET' || method === 'HEAD';
  const isGetSession = requestUrl(input).includes('/get-session');
  const requestInit = await withNativeAuth(init);

  let lastError: unknown;
  for (let attempt = 0; attempt <= SESSION_FETCH_MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(input, requestInit);
      await captureNativeAuthToken(response);
      const isTransientStatus = response.status >= 500 || response.status === 429;
      if (isIdempotent && isTransientStatus && attempt < SESSION_FETCH_MAX_RETRIES) {
        await wait(SESSION_FETCH_RETRY_BASE_MS * 2 ** attempt);
        continue;
      }
      if (isGetSession) {
        reportGetSessionOutcome(response, attempt);
      }
      return response;
    } catch (error) {
      lastError = error;
      const aborted = init?.signal?.aborted === true;
      if (isGetSession && !aborted) {
        reportClientDiag('get-session-network-error', { attempt, message: String(error) });
      }
      if (isIdempotent && !aborted && attempt < SESSION_FETCH_MAX_RETRIES) {
        await wait(SESSION_FETCH_RETRY_BASE_MS * 2 ** attempt);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};

export const authClient = createAuthClient({
  baseURL: API_URL,
  // Ensure credentials (cookies) are sent with cross-origin requests
  fetchOptions: {
    credentials: 'include',
    // Retry transient session-fetch failures so a flaky mobile connection doesn't
    // surface as a false "logged out" / login screen. See retryingFetch above.
    customFetchImpl: retryingFetch,
  },
  plugins: [
    inferAdditionalFields({
      session: {
        is_membership_active: {
          type: 'boolean',
        },
        is_admin: {
          type: 'boolean',
        },
        report_generation_limit: {
          type: 'number',
        },
      },
    }),
  ],
});

// Export types for TypeScript
export type Session = typeof authClient.$Infer.Session;
